import { useState, useEffect } from 'react';
import { trackService, TracksListResponse } from 'data/services/trackService';

export const useMyLearningTracks = (filters: {
  page?: number;
  limit?: number;
  sort?: string;
  match?: string;
  filterBy?: any[];
} = {}) => {
  const [data, setData] = useState<TracksListResponse | null>(null);
  const [filteredData, setFilteredData] = useState<TracksListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMyLearningTracks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await trackService.getMyLearningTracks({
        page: filters.page ?? 0,
        limit: filters.limit ?? 20,
        sort: filters.sort ?? 'CreatedAt desc',
        match: filters.match,
        filterBy: filters.filterBy,
      });
      setData(response);
      setFilteredData(response);
    } catch (err: any) {
      console.error('Erro ao buscar trilhas de aprendizado:', err);
      setError(err?.response?.data?.message || 'Erro ao carregar trilhas de aprendizado');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = (searchTerm: string = '', sortBy: string = 'Title asc') => {
    if (!data) return;

    let filteredTracks = [...(data.rows || [])];

    if (searchTerm.trim()) {
      filteredTracks = filteredTracks.filter(track =>
        track.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        track.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (sortBy.toLowerCase() === 'title asc') {
      filteredTracks.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy.toLowerCase() === 'createdat desc' || sortBy.toLowerCase() === 'updated_at desc') {
      filteredTracks.sort((a, b) => {
        const dateA = new Date((a as any).updatedAt || a.createdAt).getTime();
        const dateB = new Date((b as any).updatedAt || b.createdAt).getTime();
        return dateB - dateA;
      });
    }

    setFilteredData({
      ...data,
      rows: filteredTracks,
      totalRows: filteredTracks.length
    });
  };

  useEffect(() => {
    fetchMyLearningTracks();
  }, [filters.page, filters.limit, filters.sort, filters.match, JSON.stringify(filters.filterBy)]);

  const refetch = () => {
    fetchMyLearningTracks();
  };

  const startTrack = async (trackId: string): Promise<boolean> => {
    try {
      await trackService.startTrack(trackId);
      await refetch();
      return true;
    } catch (error: any) {
      console.error('Erro ao iniciar trilha:', error);
      setError(error?.response?.data?.message || 'Erro ao iniciar trilha');
      return false;
    }
  };

  const getTrackById = (trackId: string) => {
    return data?.rows?.find(track => track.id === trackId);
  };

  const getTotalTracks = (): number => {
    return data?.rows?.length || 0;
  };

  const getTotalLessons = (): number => {
    return data?.rows?.reduce((sum, track) => sum + (track.numberOfLessons || 0), 0) || 0;
  };

  const getTotalMinutes = (): number => {
    return data?.rows?.reduce((sum, track) => sum + (track.totalOfMinutes || 0), 0) || 0;
  };

  return {
    data: filteredData,
    tracks: filteredData?.rows || [],
    isLoading,
    error,
    
    // Funções de ação
    refetch,
    startTrack,
    applyFilters,
    
    // Funções de busca e filtro
    getTrackById,
    
    // Estatísticas
    getTotalTracks,
    getTotalLessons,
    getTotalMinutes,
    
    // Status helpers
    hasTracks: (filteredData?.rows?.length || 0) > 0,
  };
};
